import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Tabs as MuiTabs } from '@mui/material';
import { Content, Count, StickyTabs, Wrapper } from './Tabs.styles';
import { selectNavigationState } from 'store/entities/navigation/selectors';
import type { TablePersistentState } from 'components/common/Table/Table';
import { uiStateActions } from 'store/entities/ui';
import { DroppableTab } from './DroppableTab/DroppableTab';
import { getDroppableTabId } from './DroppableTab/utils/droppableTab';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';

export type TabId = number | string | null;

const isSameTabId = (left: TabId, right: TabId) => String(left) === String(right);

export interface Props {
  tabs: {
    id: TabId;
    label: string | React.ReactNode;
    path: string;
    content: React.ReactNode;
    disable?: boolean;
    hide?: boolean;
    count?: number;
    dataTestId?: string;
  }[];
  selectedTab: TabId;
  onTabClick: (tab: TabId) => void;
  contentContainerClassName?: string;
  tableId?: string;
  isDroppable?: boolean;
  isSortable?: boolean;
  sectionId?: string;
  sectionName?: string;
  keepAllContentMounted?: boolean;
  scrollable?: boolean;
  sticky?: boolean;
  stickyTop?: number;
  showSingleTab?: boolean;
}

const TabsBase = ({
  tabs,
  selectedTab,
  onTabClick,
  contentContainerClassName,
  tableId = '',
  isDroppable,
  isSortable,
  sectionId,
  sectionName,
  keepAllContentMounted,
  scrollable = true,
  sticky = false,
  stickyTop,
  showSingleTab = false,
}: Props) => {
  const savedNavigationState = useSelector(selectNavigationState<TablePersistentState>(`table-${tableId}`));
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const getContent = useMemo(() => {
    if (keepAllContentMounted) return null;
    const tab = tabs?.find(({ id }) => isSameTabId(id, selectedTab));
    return tab?.content || null;
  }, [keepAllContentMounted, selectedTab, tabs]);

  const getCount = useCallback((count?: number) => {
    if (!count) return {};
    return {
      icon: <Count>{count}</Count>,
    };
  }, []);

  useEffect(() => {
    if (!tableId) return;

    if (savedNavigationState?.other?.tabId) {
      const tab = tabs.find(({ id }) => id === savedNavigationState?.other?.tabId);
      if (tab) onTabClick(tab.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ref.current) {
      const height = ref.current.getBoundingClientRect().height;
      dispatch(uiStateActions.setComponentHeight({ id: 'user-modules-agenda-list-tabs', height }));
    }

    return () => {
      dispatch(uiStateActions.removeComponentHeight('user-modules-agenda-list-tabs'));
    };
  }, [dispatch]);

  const sortableItems = useMemo(() => (isSortable ? tabs.map((tab) => getDroppableTabId(tab.id, sectionId)) : []), [isSortable, tabs, sectionId]);

  const getTabKey = useCallback((id: TabId) => String(id), []);

  const renderTabs = (tab: (typeof tabs)[number]) => {
    if (isSortable || isDroppable) {
      return (
        <DroppableTab
          key={`${tab.id}-${sectionId}`}
          tab={tab}
          sectionId={sectionId}
          sectionName={sectionName}
          getCount={getCount}
          onTabClick={onTabClick}
          sortable={isSortable}
        />
      );
    }

    const count = getCount(tab.count);
    return (
      <Tab
        sx={{ textTransform: 'initial', fontSize: '13px' }}
        {...count}
        iconPosition="end"
        key={getTabKey(tab.id)}
        label={tab.label}
        value={tab.id}
        disabled={tab.disable}
        hidden={tab.hide}
        data-testid={tab.dataTestId}
      />
    );
  };

  const TabsComponent = sticky ? StickyTabs : MuiTabs;

  const muiTabs = (
    <TabsComponent
      ref={ref}
      value={selectedTab}
      variant={scrollable ? 'scrollable' : 'standard'}
      scrollButtons={scrollable ? 'auto' : false}
      hidden={!showSingleTab && tabs.filter((tab) => !tab.hide).length <= 1}
      allowScrollButtonsMobile={scrollable}
      {...(sticky ? { stickyTop: stickyTop ?? 0 } : {})}
      onChange={(_, value) => {
        if (isSortable) return;
        if (value === selectedTab) return;

        onTabClick(value);
      }}>
      {tabs?.filter((tab) => !tab.hide).map(renderTabs)}
    </TabsComponent>
  );

  return (
    <Wrapper>
      {isSortable ? (
        <SortableContext items={sortableItems} strategy={horizontalListSortingStrategy}>
          {muiTabs}
        </SortableContext>
      ) : (
        muiTabs
      )}

      <Content className={contentContainerClassName}>
        {keepAllContentMounted
          ? tabs
              .filter((tab) => !tab.hide)
              .map((tab) => (
                <div key={getTabKey(tab.id)} style={{ display: isSameTabId(tab.id, selectedTab) ? undefined : 'none' }}>
                  {tab.content}
                </div>
              ))
          : getContent}
      </Content>
    </Wrapper>
  );
};

export const Tabs = memo(TabsBase);
